import { SITE_CONFIG } from "../../app/config";
import { useHead, buildDefinedTermSchema, buildBreadcrumbSchema } from "../../seo";
import { glossaryBySlug } from "./glossary-data";

type GlossaryTermPageProps = {
  slug: string;
};

export const GlossaryTermPage = ({ slug }: GlossaryTermPageProps): JSX.Element => {
  const term = glossaryBySlug.get(slug);

  if (!term) {
    return (
      <div className="shell">
        <div className="pseo-page">
          <header className="pseo-header">
            <a href="/glossary" className="blog-back">← All Terms</a>
            <h1 className="pseo-title">Term Not Found</h1>
            <p className="pseo-subtitle">
              This glossary term doesn't exist.{" "}
              <a href="/glossary">Browse all terms →</a>
            </p>
          </header>
        </div>
      </div>
    );
  }

  const relatedTerms = term.relatedTerms
    .map((relSlug) => glossaryBySlug.get(relSlug))
    .filter(Boolean);

  useHead({
    title: `What is ${term.term}? — Music Glossary`,
    description: term.shortDefinition,
    path: `/glossary/${term.slug}`,
    ogType: "article",
    jsonLd: [
      buildDefinedTermSchema({
        term: term.term,
        definition: term.shortDefinition,
        slug: term.slug,
        category: term.category,
      }),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Glossary", url: "/glossary" },
        { name: term.term, url: `/glossary/${term.slug}` },
      ]),
    ],
  });

  return (
    <div className="shell">
      <div className="pseo-page">
        <header className="pseo-header">
          <a href="/glossary" className="blog-back">← All Terms</a>
          <div className="glossary-term-badge">{term.category}</div>
          <h1 className="pseo-title">{term.term}</h1>
          <p className="glossary-short-definition">{term.shortDefinition}</p>
        </header>

        <article className="glossary-content">
          {term.definition.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}

          {term.funFact && (
            <div className="glossary-fun-fact">
              <span className="glossary-fun-fact-icon">💡</span>
              <div>
                <strong>Did you know?</strong>
                <p>{term.funFact}</p>
              </div>
            </div>
          )}
        </article>

        {relatedTerms.length > 0 && (
          <section className="glossary-related">
            <h2>Related Terms</h2>
            <div className="glossary-related-grid">
              {relatedTerms.map((related) =>
                related ? (
                  <a
                    key={related.slug}
                    href={`/glossary/${related.slug}`}
                    className="glossary-related-card"
                  >
                    <strong>{related.term}</strong>
                    <p>{related.shortDefinition.slice(0, 100)}...</p>
                  </a>
                ) : null
              )}
            </div>
          </section>
        )}

        <footer className="pseo-footer">
          <div className="blog-cta">
            <h3>Ready to Start Your Collection?</h3>
            <p>
              Paste your Spotify playlist and discover where to buy your favorite albums on vinyl
              and CD.
            </p>
            <a href="/" className="blog-cta-button">
              Try {SITE_CONFIG.name} Free →
            </a>
          </div>
          <div className="pseo-nav-links">
            <a href="/glossary">← All Terms</a>
            <a href="/blog">Blog</a>
            <a href="/">Home</a>
          </div>
        </footer>
      </div>
    </div>
  );
};
